import type { DuplicateProjectMaterialOverrides, ProjectMaterialUsage } from '@kreart/shared';

/**
 * Form state behind the clone dialog's "Materiale" section.
 *
 * One picker per material slot, keyed by SOURCE row id, each starting on the
 * row's current board — so "changed" is simply "differs from the usage
 * snapshot", and collecting the request payload is a diff, not bookkeeping.
 * PIECES cabinets expose one picker per DISTINCT board in their cut list
 * (`p`, keyed by the board being replaced), because their boards live on the
 * pieces, not on the row.
 *
 * `swap` holds the quick-replace row per catalog board; its value is what the
 * board was last swapped to, and applying it just fills the per-row pickers —
 * the payload never carries it.
 */
export interface CloneMaterialFormValues {
  cab: Record<string, { m: string; f: string; b: string; p: Record<string, string> }>;
  drw: Record<string, { m: string; f: string }>;
  pan: Record<string, { m: string; f: string }>;
  swap: Record<string, string>;
}

export function cloneMaterialInitialValues(usage: ProjectMaterialUsage): CloneMaterialFormValues {
  const values: CloneMaterialFormValues = { cab: {}, drw: {}, pan: {}, swap: {} };
  for (const c of usage.cabinets) {
    values.cab[c.id] = {
      m: c.materialId ?? '',
      f: c.frontMaterialId ?? '',
      b: c.backMaterialId ?? '',
      p: Object.fromEntries(c.pieceMaterialIds.map((id) => [id, id])),
    };
  }
  for (const d of usage.drawers) {
    values.drw[d.id] = { m: d.materialId ?? '', f: d.frontMaterialId ?? '' };
  }
  for (const p of usage.panels) {
    values.pan[p.id] = { m: p.materialId ?? '', f: p.frontMaterialId ?? '' };
  }
  return values;
}

/** A slot override: a board was picked and it is not the one already there. */
function changed(next: string | undefined, current: string | null): string | undefined {
  const value = next ?? '';
  if (!value) return undefined;
  return value === (current ?? '') ? undefined : value;
}

/**
 * Diff the section's pickers against the usage snapshot into the request
 * payload. Returns undefined when nothing changed, which keeps the copy
 * byte-for-byte faithful. Tolerates partially hydrated values (`useWatch`
 * hands back deep partials while the form is settling).
 */
export function collectMaterialOverrides(
  usage: ProjectMaterialUsage,
  values: Partial<CloneMaterialFormValues> | undefined,
): DuplicateProjectMaterialOverrides | undefined {
  if (!values) return undefined;
  const overrides: Required<DuplicateProjectMaterialOverrides> = {
    cabinets: [],
    drawers: [],
    panels: [],
  };

  for (const c of usage.cabinets) {
    const row = values.cab?.[c.id];
    if (!row) continue;
    const materialId = changed(row.m, c.materialId);
    const frontMaterialId = changed(row.f, c.frontMaterialId);
    const backMaterialId = changed(row.b, c.backMaterialId);
    const pieceMaterials = c.pieceMaterialIds.flatMap((fromMaterialId) => {
      const toMaterialId = changed(row.p?.[fromMaterialId], fromMaterialId);
      return toMaterialId ? [{ fromMaterialId, toMaterialId }] : [];
    });
    if (materialId || frontMaterialId || backMaterialId || pieceMaterials.length) {
      overrides.cabinets.push({
        id: c.id,
        ...(materialId ? { materialId } : {}),
        ...(frontMaterialId ? { frontMaterialId } : {}),
        ...(backMaterialId ? { backMaterialId } : {}),
        ...(pieceMaterials.length ? { pieceMaterials } : {}),
      });
    }
  }
  for (const d of usage.drawers) {
    const row = values.drw?.[d.id];
    if (!row) continue;
    const materialId = changed(row.m, d.materialId);
    const frontMaterialId = changed(row.f, d.frontMaterialId);
    if (materialId || frontMaterialId) {
      overrides.drawers.push({
        id: d.id,
        ...(materialId ? { materialId } : {}),
        ...(frontMaterialId ? { frontMaterialId } : {}),
      });
    }
  }
  for (const p of usage.panels) {
    const row = values.pan?.[p.id];
    if (!row) continue;
    const materialId = changed(row.m, p.materialId);
    const frontMaterialId = changed(row.f, p.frontMaterialId);
    if (materialId || frontMaterialId) {
      overrides.panels.push({
        id: p.id,
        ...(materialId ? { materialId } : {}),
        ...(frontMaterialId ? { frontMaterialId } : {}),
      });
    }
  }

  if (!overrides.cabinets.length && !overrides.drawers.length && !overrides.panels.length) {
    return undefined;
  }
  return {
    ...(overrides.cabinets.length ? { cabinets: overrides.cabinets } : {}),
    ...(overrides.drawers.length ? { drawers: overrides.drawers } : {}),
    ...(overrides.panels.length ? { panels: overrides.panels } : {}),
  };
}

/** How many components (rows) reference a board anywhere — the quick-swap badge. */
export function materialComponentCount(usage: ProjectMaterialUsage, materialId: string): number {
  let count = 0;
  for (const c of usage.cabinets) {
    if (
      c.materialId === materialId ||
      c.frontMaterialId === materialId ||
      c.backMaterialId === materialId ||
      c.pieceMaterialIds.includes(materialId)
    ) {
      count += 1;
    }
  }
  for (const d of usage.drawers) {
    if (d.materialId === materialId || d.frontMaterialId === materialId) count += 1;
  }
  for (const p of usage.panels) {
    if (p.materialId === materialId || p.frontMaterialId === materialId) count += 1;
  }
  return count;
}

/**
 * Every picker path whose SOURCE board is `fromMaterialId` — what one
 * quick-swap pick overwrites. Deliberately matched against the usage snapshot,
 * not the live form: a swap always means "everything that WAS on this board",
 * deterministically, even after individual rows were hand-adjusted.
 */
export function swapTargetPaths(usage: ProjectMaterialUsage, fromMaterialId: string): string[] {
  const paths: string[] = [];
  for (const c of usage.cabinets) {
    if (c.materialId === fromMaterialId) paths.push(`cab.${c.id}.m`);
    if (c.frontMaterialId === fromMaterialId) paths.push(`cab.${c.id}.f`);
    if (c.backMaterialId === fromMaterialId) paths.push(`cab.${c.id}.b`);
    if (c.pieceMaterialIds.includes(fromMaterialId)) paths.push(`cab.${c.id}.p.${fromMaterialId}`);
  }
  for (const d of usage.drawers) {
    if (d.materialId === fromMaterialId) paths.push(`drw.${d.id}.m`);
    if (d.frontMaterialId === fromMaterialId) paths.push(`drw.${d.id}.f`);
  }
  for (const p of usage.panels) {
    if (p.materialId === fromMaterialId) paths.push(`pan.${p.id}.m`);
    if (p.frontMaterialId === fromMaterialId) paths.push(`pan.${p.id}.f`);
  }
  return paths;
}

/** The number of components an overrides payload touches — the header badge. */
export function overriddenComponentCount(
  overrides: DuplicateProjectMaterialOverrides | undefined,
): number {
  if (!overrides) return 0;
  return (
    (overrides.cabinets?.length ?? 0) +
    (overrides.drawers?.length ?? 0) +
    (overrides.panels?.length ?? 0)
  );
}
